import Head from "next/head"
import { IMAGE_SIZES } from "@/constants/ui"
import MainLayout, { getMainLayout } from "@/layouts/main"
import { EventActorRef, isEventActorRef } from "@/types/entity"
import { EventActorPageProps } from "@/types/page"

import cloudflareLoader from "@/lib/cloudflare-loader"
import { fetchEventActorProps } from "@/lib/event-actor"
import { MtSSP } from "@/lib/mt-server-side-props"
import { BASE_CANONICAL_URL } from "@/lib/utils/constants"
import { EventActorMain } from "@/components/event-actor-main"
import { IOSAppBannerMeta } from "@/components/ios-app-banner-meta"

function EventActorDetailPage(pageProps: EventActorPageProps) {
  const { eventActor } = pageProps
  const title = `${eventActor.name} - Events & Tour Dates`
  const desc = eventActor.description
    ? eventActor.description.substring(0, 160)
    : `Find upcoming events and tour dates for ${eventActor.name}. Get tickets and event information.`

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        {eventActor.image_url && (
          <meta
            property="og:image"
            content={cloudflareLoader({
              src: eventActor.image_url,
              width: IMAGE_SIZES.xl,
            })}
            key="og-image"
          />
        )}
        <meta name="twitter:card" content="summary_large_image" key="tw-card" />
        <IOSAppBannerMeta /*appArgument={eventActor.canonical_url} */ />
      </Head>
      <MainLayout>
        <EventActorMain {...pageProps} />
      </MainLayout>
    </>
  )
}

export const getServerSideProps = MtSSP.asAuthPage<EventActorPageProps>({
  handler: async ({ serverAxios, baseProps, context }) => {
    const eventActorRef = context.params?.["slug"]?.[
      context.params?.["slug"]?.length - 1
    ] as EventActorRef

    if (!eventActorRef || !isEventActorRef(eventActorRef)) {
      return { notFound: true }
    }

    const [appProps, { eventActor }] = await Promise.all([
      baseProps(),
      fetchEventActorProps(eventActorRef, serverAxios),
    ])

    if (eventActor == null) {
      return { notFound: true }
    }

    if (!context.req.url?.startsWith("/_next/")) {
      // do a permanent redirect when the url is not the canonical one
      const canonical_relative_url = eventActor.canonical_url.replace(
        BASE_CANONICAL_URL,
        ""
      )
      if (canonical_relative_url !== context.req.url?.split("?")[0]) {
        const destination = context.req.url?.split("?")[1]
          ? canonical_relative_url + "?" + context.req.url?.split("?")[1]
          : canonical_relative_url
        console.log(
          `redirecting to canonical url ${canonical_relative_url} mismatch from requested ${context.req.url?.split("?")[0]}`
        )
        return {
          redirect: {
            destination: destination,
            permanent: true,
          },
        }
      }
    }

    return {
      props: {
        ...appProps,
        eventActor,
      },
    }
  },
})

EventActorDetailPage.getLayout = getMainLayout

export default EventActorDetailPage
